/**
 * Redux Middleware for Session Debug Logging
 *
 * This middleware automatically tracks Redux actions related to session loading
 * and provides detailed state transitions for debugging purposes.
 */

import { sessionDebugger } from '../../utilities/session-debug';
import { sessionConstants } from '../_constants';

export const sessionDebugMiddleware = (store) => (next) => (action) => {
  const stateBefore = store.getState();
  const startTime = performance.now();

  // Execute the action
  const result = next(action);

  const stateAfter = store.getState();
  const duration = performance.now() - startTime;

  // Track the action if debugging is enabled
  if (sessionDebugger.isEnabled) {
    sessionDebugger.trackReduxAction(action, stateAfter, stateBefore);

    // Track specific session loading actions with enhanced logging
    switch (action.type) {
      case sessionConstants.LOAD_REQUEST:
        sessionDebugger.trackStep('REDUX_LOAD_START', {
          sessionKey: action.key,
          previousState: stateBefore.session?.loadingState,
        });
        break;

      case sessionConstants.LOAD_SUCCESS:
        sessionDebugger.trackStep('REDUX_LOAD_SUCCESS', {
          sessionId: action.session?.id,
          sessionName: action.session?.name,
          role: action.role,
          hasSessionData: !!action.sessionData,
          teamCount: action.sessionData?.teams?.allIds?.length || 0,
          lineupCount: action.sessionData?.lineups?.allIds?.length || 0,
        });
        break;

      case sessionConstants.LOAD_FAILURE:
        sessionDebugger.trackStep('REDUX_LOAD_ERROR', {
          loadingState: action.loadingState,
          error: 'Session load failed',
        });
        break;

      case 'INITIALIZE': // Producer initialize
        sessionDebugger.trackStep('PRODUCER_INIT_SUCCESS', {
          hasTeamA: !!stateAfter.producer?.teamA,
          hasTeamB: !!stateAfter.producer?.teamB,
          loaded: stateAfter.producer?.loaded,
          round: stateAfter.producer?.round,
        });
        break;

      default:
        // No specific tracking for other action types
        break;
    }

    // Track performance for slow actions
    if (duration > 10) {
      // Log actions that take more than 10ms
      sessionDebugger.trackPerformance(`redux_${action.type}`, duration, {
        actionType: action.type,
        payloadSize: JSON.stringify(action).length,
      });
    }
  }

  return result;
};

export default sessionDebugMiddleware;
